import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Typography,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Checkbox } from '@mui/material';
import { groupProp } from '../../../redux/ChartFilterGroup/ChartFilterGroupInterface';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import { palette } from '../../../';
import { AlertColor } from '@mui/material/Alert';
import {
  setSyncFilterGroup,
  setLastModifiedFilter,
  setOptionsAndSelections,
  setFiltersOfGroup,
} from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import Logger from '../../../Logger';
import FetchData, { IAPIResponse } from '../../ServerCall/FetchData';
import { IChartFIlter } from '../interfaces';
import { InfoOutlined } from '@mui/icons-material';
import { fontSize } from '../../../';
import { IDataset } from '../../DataSet/types/types';
import Cookies from 'js-cookie';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import { updateChartFilterAnyConditionMatch } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { Divider } from '@mui/material';

interface Props {
  group: groupProp;
  anchorElSyncFilter: HTMLButtonElement | null;
  setAnchorElSyncFilter: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
  setSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setOpenAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setTestMessage: React.Dispatch<React.SetStateAction<string>>;
  link: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * rendered by ChartFilterGroups.tsx
 * @returns a menu containing functionality of enabling syncFilter. It opens when clicking more icon in GroupHeader while screen is not dashboard.
 */
const SyncFilterMenu = ({
  group,
  anchorElSyncFilter,
  setAnchorElSyncFilter,
  setSeverity,
  setOpenAlert,
  setTestMessage,
  link,
  setLoading,
}: Props) => {
  const [showFilterProperties, setShowFilterProperties] = useState<boolean>(false);
  // Always get the latest group state from Redux

  const {
    tabTileProps,
    groups,
    chartGroup,
    showDash,
    callQueryAPIForDashboard,
    dashBoardGroup,
    conditionType,
  } = useSelector((state: RootState) => ({
    tabTileProps: state.tabTileProps,
    groups: state.chartFilterGroup.groups,
    chartGroup: state.chartFilterGroup,
    showDash: state.tabTileProps.showDash,
    callQueryAPIForDashboard: state.tabTileProps.callQueryAPIDashboard,
    dashBoardGroup: state.dashBoardFilterGroup,
    conditionType:
      state.chartFilterGroup.groups[group.id]?.any_condition_match !== false ? 'ANY' : 'ALL',
  }));

  const dispatch = useDispatch();

  const dsDetails = tabTileProps.selectedDataSetList.find(
    (dataset: any) => dataset.id === group.dsIdForGroup,
  );

  async function modifyOptions(syncEnabled: boolean) {
    Logger('info', `Sync filter enabled:${syncEnabled}`);
    if (!syncEnabled) {
      const filters = group.filters;
      if (!filters || filters.length < 1) return;
      const pickListIndices: number[] = [];
      const promises: Promise<IAPIResponse<Record<string, any>>>[] = [];
      filters.forEach((filter: IChartFIlter, idx: number) => {
        if (filter.fieldtypeoption === 'Pick List') {
          pickListIndices.push(idx);
          const payload = {
            tableId: filter.tableId,
            fieldName: filter.displayname,
            dataType: filter.dataType,
            filterOption: 'allValues',
            ...(filter.dataType === 'timestamp' || filter.dataType === 'date'
              ? { timeGrain: filter.prefix ?? 'year' }
              : {}),
          };

          let url = '';

          if (link && link !== '') {
            url = `open-link/filter-options?datasetid=${
              dsDetails.id
            }&workspaceId=${dsDetails.workSpaceId}&${
              dsDetails.isFlatFileData ? '' : `dbconnectionid=${dsDetails.connectionId}`
            }&linkId=${link}`;
          } else {
            url = `filter-options?datasetid=${dsDetails.id}&workspaceId=${dsDetails.workSpaceId}&${
              dsDetails.isFlatFileData ? '' : `dbconnectionid=${dsDetails.connectionId}`
            }`;
          }

          promises.push(
            FetchData({
              checkToken: link === '',
              method: 'POST',
              url: url,
              requestType: 'withData',
              headers:
                link && link !== ''
                  ? {
                      'Content-Type': 'application/json',
                      'X-TENANT-ID': 'community',
                    }
                  : {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${Cookies.get('authToken')}`,
                    },
              data: payload,
            }),
          );
        }
      });
      if (promises.length > 0) {
        try {
          setLoading(true);
          const res = await Promise.all(promises);
          res.forEach((response: IAPIResponse<Record<string, any>>, idx: number) => {
            if (response.status && response.data) {
              const avalableOptions = response.data[Object.keys(response.data)[0]];
              const result = avalableOptions.map((value: any) => {
                return value !== null
                  ? typeof value === 'boolean'
                    ? value
                      ? 'True'
                      : 'False' // Convert boolean to string
                    : value
                  : '(Blank)';
              });
              const options = ['(All)', ...result];
              const filterIndex = pickListIndices[idx];
              filters[filterIndex].userSelection = options;
              filters[filterIndex].rawselectmembers = options;
              dispatch(
                setOptionsAndSelections(group.id, options, options, filters[filterIndex].uId),
              );
            }
          });
          dispatch(setFiltersOfGroup(group.id, filters));
        } catch (e) {
          setLoading(false);
        } finally {
          setLoading(false);
        }
      }
    } else {
      const filters = group.filters;
      if (!filters || filters.length === 0) return;
      filters.forEach((filter: IChartFIlter, idx: number) => {
        if (filter.fieldtypeoption === 'Pick List') {
          const options = filter.rawselectmembersOriginal;
          dispatch(
            setOptionsAndSelections(group.id, options, filter.userSelection ?? options, filter.uId),
          );
          dispatch(setLastModifiedFilter(group.id, filter.uId));
        }
      });
    }
  }
  const handleAnyConditionMatchModify = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as 'ALL' | 'ANY';
    dispatch(updateChartFilterAnyConditionMatch(group.id, value === 'ANY'));
    const callFilters = chartGroup.groups[group.id]?.filters?.length ?? 0 > 0;
    if (callFilters) callAPI();
  };

  const callAPI = () => {
    if (showDash) {
      Object.keys(chartGroup.tabTile).forEach((tabTileId: string) => {
        if (dashBoardGroup.filterGroupTabTiles[group.id].includes(tabTileId)) {
          dispatch(callQueryAPI(tabTileId, true));
        }
      });
    }
    Object.entries(chartGroup.tabTile).forEach(([tabTileId, arr]: any) => {
      if (arr.includes(group.id)) {
        dispatch(callQueryAPI(tabTileId, true));
        if (!callQueryAPIForDashboard) dispatch(callQueryAPIDashboard(true));
      }
    });
    if (showDash && !callQueryAPIForDashboard) dispatch(callQueryAPIDashboard(true));
  };

  return (
    <>
      <Menu
        id="simple-menu"
        anchorEl={anchorElSyncFilter}
        open={Boolean(anchorElSyncFilter)}
        onClose={() => setAnchorElSyncFilter(null)}
        sx={{
          '.MuiMenu-list': {
            paddingBlock: 0,
            width: '150px',
          },
        }}
      >
        <MenuItem
          disableGutters
          sx={{
            width: '100%',
            height: '35px',
          }}
        >
          <Checkbox
            checked={group.syncFilter}
            onClick={async (e) => {
              if (
                group.syncFilter === false &&
                groups[group.id].filters?.some((calc: any) => calc.isCalculatedField === true)
              ) {
                setSeverity('warning');
                setOpenAlert(true);
                setTestMessage(
                  `${
                    groups[group.id].name
                  } has a calculated field.\nSync filter is not available for calculated field`,
                );
              } else {
                setAnchorElSyncFilter(null);
                await modifyOptions(!group.syncFilter);
                dispatch(setSyncFilterGroup(group.id, !group.syncFilter));
                callAPI();
              }
            }}
            sx={{
              '&.Mui-checked': {
                color: palette.secondary.light,
              },
            }}
          />
          <Typography>Sync filter</Typography>
        </MenuItem>
        <MenuItem
          disableGutters
          sx={{
            width: '100%',
            padding: '0.25rem 0.7rem',
          }}
        >
          <FormControl component="fieldset" disabled={group.syncFilter}>
            <Divider style={{ width: '120%', transform: 'translateX(-12px)' }} />
            <RadioGroup value={conditionType} onChange={handleAnyConditionMatchModify}>
              <FormControlLabel
                value="ALL"
                control={<Radio size="small" />}
                label={<Typography variant="body2">All conditions met</Typography>}
              />
              <FormControlLabel
                value="ANY"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Any condition met</Typography>}
              />
            </RadioGroup>
            <Divider style={{ width: '120%', transform: 'translateX(-12px)' }} />
          </FormControl>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorElSyncFilter(null);
            setShowFilterProperties(true);
          }}
          disableGutters
          sx={{
            // marginRight: "0.5rem",
            width: '100%',
            padding: 0,
          }}
        >
          <InfoOutlined
            sx={{
              margin: '0.5rem 0.5635rem',
            }}
          />
          <Typography>Properties</Typography>
        </MenuItem>
      </Menu>
      <Dialog
        open={showFilterProperties}
        onClose={() => setShowFilterProperties(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            fontSize: fontSize.large,
            fontWeight: '600',
          }}
        >
          Filter Properties
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <u>{group.name}</u> belongs to dataset{' '}
            <u>
              {tabTileProps.selectedDataSetList.find(
                (dataset: IDataset) => dataset.id === group.dsIdForGroup,
              )?.datasetName || 'Unknown'}
            </u>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SyncFilterMenu;
